import React, { useState, useEffect, useRef, RefObject, useContext } from 'react';
import { customerSVC } from '@com/core/common/services/api';
import { AxiosError } from 'axios';
import { reportError } from '@com/core/common/utils/sentry';
import { getMarketplaceConfig } from '@com/core/common/utils/config';
import { MARKETPLACE } from '@com/core/common/baseConfig';
import { useRouter } from 'next/router';
import { GoogleMap } from 'react-google-maps';
import isString from 'lodash/isString';
import { TCollection, TLatLng, TLocation, TDailyPropertyList } from '../types/customer_address';
import { AppContext } from './appContext';

type TProps = {
    children: JSX.Element;
    showCollectionPoints?: boolean;
    showSearch?: boolean;
    showLocateMe?: boolean;
    disableClicks?: boolean;
    mapProps?: {
        draggable?: boolean;
        zoomControl?: boolean;
        // This should ideally be GoogleMapProps, but that doesn't take in "draggable" for some reason
    };
};

const config = getMarketplaceConfig(MARKETPLACE);

export type TMapContextFunctions = {
    getLocation: () => void;
    getCollectionPoints: () => void;
    handleMapClick: (latLng: TLatLng | null) => void;
    handleMapClickDaily: (latLng: TLatLng | null) => void;
    getPropertyList: (latLng: TLatLng | null) => void;
    handleCollectionPointClick: (collectionPointCode: string | null) => void;
    enableCollectionPoints: (enable: boolean) => void;
    enableSearch: (enable: boolean) => void;
    enableLocateMe: (enable: boolean) => void;
    updateSearchText: (value: string | null) => void;
    updateError: (error: AxiosError | string | null) => void;
    removeClickedLocation: () => void;
    removeSelectedCollectionPointCode: () => void;
    resetDailyPropertyList: () => void;
    setClickedLocation: (location: TLocation) => void;
};

type TMapContextState = {
    collectionPoints: TCollection[] | null;
    selectedCollectionPoint: TCollection | null;
    collectionPointsEnabled: boolean;
    searchEnabled: boolean;
    locateMeEnabled: boolean;
    searchText: string | null;
    clickedLocation: TLocation | null;
    dailyPropertyList: TDailyPropertyList | null;
    error: AxiosError | string | null;
    mapProps?: TProps['mapProps'];
    mapRef?: RefObject<GoogleMap>;
};

const initContextState: TMapContextState & TMapContextFunctions = {
    collectionPoints: [],
    selectedCollectionPoint: null,
    collectionPointsEnabled: true,
    searchEnabled: true,
    locateMeEnabled: true,
    searchText: null,
    clickedLocation: null,
    dailyPropertyList: null,
    error: null,
    mapProps: undefined,
    mapRef: undefined,
    getLocation: async (): Promise<void> => undefined,
    getCollectionPoints: async (): Promise<void> => undefined,
    handleMapClick: async (): Promise<void> => undefined,
    handleMapClickDaily: async (): Promise<void> => undefined,
    getPropertyList: async (): Promise<void> => undefined,
    handleCollectionPointClick: async (): Promise<void> => undefined,
    enableCollectionPoints: async (): Promise<void> => undefined,
    enableSearch: async (): Promise<void> => undefined,
    enableLocateMe: async (): Promise<void> => undefined,
    updateSearchText: async (): Promise<void> => undefined,
    updateError: async (): Promise<void> => undefined,
    removeClickedLocation: async (): Promise<void> => undefined,
    removeSelectedCollectionPointCode: async (): Promise<void> => undefined,
    resetDailyPropertyList: async (): Promise<void> => undefined,
    setClickedLocation: (): void => undefined,
};

// eslint-disable-next-linevariable-name
export const MapContext = React.createContext(initContextState);

// eslint-disable-next-linevariable-name
export const MapContextProvider = ({
    children,
    disableClicks = false,
    mapProps,
    showCollectionPoints = true,
    showLocateMe = true,
    showSearch = true,
}: TProps): React.ReactElement => {
    const { query } = useRouter();
    const collectionPointState = query.collectionPoints as string;

    const { locale } = useContext(AppContext);

    const mapRef = useRef(null);

    const shouldShowCollectionPoints: boolean =
        locale.seoRegion !== 'egypt' && collectionPointState !== 'hide' && showCollectionPoints;

    const [collectionPoints, setCollectionPoints] = useState<TCollection[] | null>([]);
    const [selectedCollectionPoint, setSelectedCollectionPoint] = useState<TCollection | null>(null);
    const [collectionPointsEnabled, setCollectionPointsEnabled] = useState<boolean>(shouldShowCollectionPoints);
    const [searchEnabled, setSearchEnabled] = useState<boolean>(showSearch);
    const [locateMeEnabled, setLocateMeEnabled] = useState<boolean>(showLocateMe);
    const [searchText, setSearchText] = useState<string | null>(null);
    const [clickedLocation, setClickedLocation] = useState<TLocation | null>(null);
    const [dailyPropertyList, setDailyPropertyList] = useState<TDailyPropertyList | null>(null);
    const [error, setError] = useState<AxiosError | string | null>(null);

    const getCollectionPoints = async (): Promise<void> => {
        try {
            const { data } = await customerSVC(locale.apiLocale).address.getCollectionPoints();
            if (data && data.rows && data.rows.length) {
                setCollectionPoints(data.rows);
            }
        } catch (err) {
            if (isString(err.error)) {
                setError(err.error);
            } else if (err.error && err.error.detail && isString(err.error.detail)) {
                setError(err.error.detail);
            } else {
                setError('Could not get Collection Points');
            }
            reportError(err, { componentStack: 'mapContext.getCollectionPoints' });
        }
    };

    useEffect(() => {
        if (showCollectionPoints) {
            getCollectionPoints();
        }
    }, []);

    const handleMapClick = async (latLng: TLatLng | null): Promise<void> => {
        if (disableClicks) {
            return;
        }

        try {
            setClickedLocation(null);
            if (latLng) {
                await customerSVC(locale.apiLocale)
                    .address.getAreaFromLocation(latLng.lat * config.map.precision, latLng.lng * config.map.precision)
                    .then(data => {
                        setSelectedCollectionPoint(null);
                        setClickedLocation(data.data);
                        setSearchText(`${data.data.area} - ${data.data.city.name}`);
                    });
            }
        } catch (err) {
            if (isString(err.error)) {
                setError(err.error);
            }
            reportError(err, { componentStack: 'mapContext.handleMapClick' });
        }
    };

    // DAILY SPECIFICS START
    const handleMapClickDaily = async (latLng: TLatLng | null): Promise<void> => {
        try {
            setClickedLocation(null);
            if (latLng) {
                await customerSVC(locale.apiLocale)
                    .address.getDailyAreaBrowseFromLocation(
                        latLng.lat * config.map.precision,
                        latLng.lng * config.map.precision,
                    )
                    .then(data => {
                        setClickedLocation(data.data);
                        setSearchText(`${data.data.area} - ${data.data.city.name}`);
                    });
            }
        } catch (err) {
            if (isString(err.error)) {
                setError(err.error);
            }
            reportError(err, { componentStack: 'mapContext.handleMapClickDaily' });
        }
    };

    const getPropertyList = async (latLng: TLatLng | null): Promise<void> => {
        try {
            if (latLng) {
                const propertiesData = {
                    lat: latLng.lat,
                    lng: latLng.lng,
                };
                await customerSVC(locale.apiLocale)
                    .address.getDailyProperties(propertiesData)
                    .then(data => {
                        setDailyPropertyList(data.data.property_list);
                    });
            }
        } catch (err) {
            if (isString(err.error)) {
                setError(err.error);
            }
            reportError(err, { componentStack: 'mapContext.getPropertyList' });
        }
    };

    const resetDailyPropertyList = async (): Promise<void> => {
        setDailyPropertyList(null);
    };

    // DAILY SPECIFICS END

    const handleCollectionPointClick = async (collectionPointCode: string | null): Promise<void> => {
        setClickedLocation(null);
        const collectionPoint = collectionPoints?.find(point => point.collectionPointCode === collectionPointCode);
        setSelectedCollectionPoint(collectionPoint || null);
    };

    const enableCollectionPoints = async (enabled: boolean): Promise<void> => {
        setCollectionPointsEnabled(enabled);
    };

    const enableSearch = async (enabled: boolean): Promise<void> => {
        setSearchEnabled(enabled);
    };

    const enableLocateMe = async (enabled: boolean): Promise<void> => {
        setLocateMeEnabled(enabled);
    };

    const updateSearchText = async (value: string | null): Promise<void> => {
        setSearchText(value);
    };

    const updateError = async (errorToUpdate: AxiosError | string | null): Promise<void> => {
        setError(errorToUpdate);
    };

    const removeClickedLocation = async (): Promise<void> => {
        setClickedLocation(null);
        setSearchText('');
    };

    const removeSelectedCollectionPointCode = async (): Promise<void> => {
        setSelectedCollectionPoint(null);
    };

    const getLocation = (): void => {
        if (disableClicks) {
            return;
        }

        if (navigator && navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                (pos: any) => {
                    const latLng = {
                        lat: pos.coords.latitude,
                        lng: pos.coords.longitude,
                    };
                    handleMapClickDaily(latLng);
                },
                (e: any) => {
                    setError('Unable to get location');
                },
            );
        }
    };

    const mapContextValue = {
        // ------props--------
        mapProps,
        mapRef,
        // ------state--------
        collectionPoints,
        selectedCollectionPoint,
        collectionPointsEnabled,
        searchEnabled,
        locateMeEnabled,
        searchText,
        clickedLocation,
        dailyPropertyList,
        error,
        // -----functions-----
        getLocation,
        getCollectionPoints,
        handleMapClick,
        handleMapClickDaily,
        getPropertyList,
        handleCollectionPointClick,
        enableCollectionPoints,
        enableSearch,
        enableLocateMe,
        updateSearchText,
        updateError,
        removeClickedLocation,
        removeSelectedCollectionPointCode,
        resetDailyPropertyList,
        setClickedLocation,
    };

    return <MapContext.Provider value={mapContextValue}>{children}</MapContext.Provider>;
};
